import React from 'react';
import { Controller } from 'react-hook-form';
import { IconArrowRight } from '@tabler/icons-react';
import { Link } from '@planne-software/uni';
import { Button } from '@planne-software/uni/Button';
import { HelperText, Input, InputLabel } from '@planne-software/uni/Input';
import { Stack } from '@planne-software/uni/mui/material';

import { useTranslate } from '@src/core/hooks';
import { VERIFICATION_CODE_LENGTH } from '@src/domains/customers';
import {
  AuthCardButtonWrapper,
  AuthCardRoot,
  AuthCardSubtitle,
  AuthCardTextGroup,
  AuthCardTitle,
  authLinkSx,
  ButtonGroup,
  OtpResendRow,
  OtpTimerText,
  SignupLinkWrapper,
} from '@src/features/customers';
import { AuthMethodDivider } from '@src/features/customers/auth/identification';

import { useOtpLoginForm } from '../hooks/useOtpLoginForm';
import { PinCodeInput } from '@src/common/ui/PinCodeInput';

type OtpLoginFormProps = {
  initialEmail: string;
  active: boolean;
  onSwitchToPassword: () => void;
  onSignup: (email: string) => void;
  onEmailChange?: (email: string) => void;
  onCodeSent?: () => void;
  onCodeValidated?: () => void;
  submitLabel?: string;
  signupLabel?: string;
};

export const OtpLoginForm = (props: OtpLoginFormProps) => {
  const { initialEmail, active, onSwitchToPassword, onSignup, onEmailChange, submitLabel, signupLabel } = props;
  const { onCodeSent, onCodeValidated } = props;
  const { i18n, i18nElement } = useTranslate();

  const {
    control,
    errors,
    loading,
    otpSending,
    isSubmitEnabled,
    email,
    onSubmit,
    resendOtp,
    otpResend,
    resendSeconds,
    requestLockedUntil,
    onEmailInputChange,
  } = useOtpLoginForm({ initialEmail, active, onEmailChange, onCodeSent, onCodeValidated });

  const showResendCountdown = Boolean(requestLockedUntil) && !otpResend.canResend && resendSeconds > 0;
  const showResendLink = Boolean(requestLockedUntil) && otpResend.canResend;
  const showOtpResendRow = otpSending || showResendCountdown || showResendLink;

  return (
    <AuthCardRoot data-testid='otp-login-form'>
      <AuthCardTextGroup>
        <AuthCardTitle>{i18n('checkout_auth_login_title')}</AuthCardTitle>
        <AuthCardSubtitle>
          {i18nElement('checkout_auth_login_otp_subtitle', { email: email || initialEmail })}
        </AuthCardSubtitle>
      </AuthCardTextGroup>

      <form onSubmit={onSubmit} noValidate>
        <AuthCardRoot>
          <Controller
            name='email'
            control={control}
            render={({ field: { value, name, ref, ...rest } }) => (
              <Input
                type='email'
                label={i18n('checkout_auth_email_label')}
                name={name}
                id={name}
                value={value ?? ''}
                error={!!errors.email}
                disabled={loading || otpSending}
                helperText={errors.email?.message}
                autoComplete='email'
                ref={ref}
                size='medium'
                {...rest}
                onChange={(event) => onEmailInputChange(event.target.value)}
              />
            )}
          />

          <Stack gap={1}>
            <InputLabel error={!!errors.otp} size='medium'>
              {i18n('checkout_auth_otp_label')}
            </InputLabel>
            <Controller
              name='otp'
              control={control}
              render={({ field: { onChange, value, name, ...rest } }) => (
                <PinCodeInput
                  value={value}
                  onChange={onChange}
                  name={name}
                  mode='numeric'
                  length={VERIFICATION_CODE_LENGTH}
                  disabled={loading || otpSending}
                  error={!!errors.otp}
                  {...rest}
                />
              )}
            />
            <HelperText error={!!errors.otp} size='medium'>
              {errors.otp?.message ?? i18n('checkout_auth_otp_helper')}
            </HelperText>
          </Stack>

          {showOtpResendRow && (
            <OtpResendRow>
              {showResendLink && !otpSending ? (
                <Link
                  component='button'
                  type='button'
                  underline='none'
                  onClick={resendOtp}
                  disabled={loading}
                  sx={authLinkSx}
                >
                  {i18n('checkout_auth_otp_resend_cta')}
                </Link>
              ) : (
                <OtpTimerText>
                  {otpSending
                    ? i18n('checkout_auth_otp_sending')
                    : i18n('checkout_auth_otp_resend_countdown', { seconds: resendSeconds })}
                </OtpTimerText>
              )}
            </OtpResendRow>
          )}

          <ButtonGroup>
            <AuthCardButtonWrapper>
              <Button
                type='submit'
                fullWidth
                loading={loading}
                disabled={!isSubmitEnabled}
                endSlot={<IconArrowRight aria-hidden />}
              >
                {submitLabel ?? i18n('auth_login_submit_cta')}
              </Button>
            </AuthCardButtonWrapper>

            <AuthCardButtonWrapper>
              <Button
                type='button'
                variant='outlined'
                fullWidth
                disabled={loading || otpSending}
                onClick={onSwitchToPassword}
                endSlot={<IconArrowRight aria-hidden />}
              >
                {i18n('checkout_auth_login_with_password_cta')}
              </Button>
            </AuthCardButtonWrapper>
          </ButtonGroup>
        </AuthCardRoot>
      </form>

      <AuthMethodDivider />

      <SignupLinkWrapper>
        <Link
          component='button'
          type='button'
          underline='none'
          onClick={() => onSignup(email || initialEmail)}
          disabled={loading}
          sx={authLinkSx}
        >
          {signupLabel ?? i18n('auth_login_signup_link')}
        </Link>
      </SignupLinkWrapper>
    </AuthCardRoot>
  );
};

export default OtpLoginForm;
